import React from "react";
import { cn } from "../../utils/cn";

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "text" | "circular" | "rectangular";
  width?: string | number;
  height?: string | number;
  animation?: "pulse" | "wave" | "none";
}

const Skeleton: React.FC<SkeletonProps> = ({
  className,
  variant = "rectangular",
  width,
  height,
  animation = "pulse",
  style,
  ...props
}) => {
  const baseClasses = "bg-gray-200 rounded-sm";

  const variantClasses = {
    text: "h-4 rounded-sm",
    circular: "rounded-full",
    rectangular: "rounded-sm",
  };

  const animationClasses = {
    pulse: "animate-pulse",
    wave: "bg-linear-to-r from-gray-200 via-gray-100 to-gray-200 bg-size-[200%_100%] animate-[shimmer_2s_infinite]",
    none: "",
  };

  const customStyle: React.CSSProperties = {
    width: width || (variant === "text" ? "100%" : undefined),
    height:
      height ||
      (variant === "circular"
        ? width
        : variant === "text"
          ? undefined
          : "1rem"),
    ...style,
  };

  return (
    <div
      className={cn(
        baseClasses,
        variantClasses[variant],
        animationClasses[animation],
        className,
      )}
      style={customStyle}
      {...props}
    />
  );
};

// Skeleton para componentes específicos
export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 3,
  className,
}) => (
  <div className={cn("space-y-2", className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        variant="text"
        width={i === lines - 1 ? "75%" : "100%"}
        className="h-4"
      />
    ))}
  </div>
);

export const SkeletonCard: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn("p-6 space-y-4", className)}>
    <Skeleton variant="rectangular" width="60%" height="1.5rem" />
    <SkeletonText lines={3} />
    <div className="flex gap-2">
      <Skeleton variant="rectangular" width="80px" height="2rem" />
      <Skeleton variant="rectangular" width="80px" height="2rem" />
    </div>
  </div>
);

export const SkeletonAvatar: React.FC<{
  size?: number;
  className?: string;
}> = ({ size = 40, className }) => (
  <Skeleton
    variant="circular"
    width={size}
    height={size}
    className={className}
  />
);

export default Skeleton;
